// API base do backend catalogo-admin.
// Em prod o nginx faz reverse proxy de /catalogo-admin/api → :3013/api,
// então usamos o prefixo /catalogo-admin. Em dev o vite proxy redireciona /api,
// por isso o base muda dependendo do ambiente.
export const API_BASE = import.meta.env.DEV ? '' : '/catalogo-admin'

export type Me = {
  id: string
  email: string | null
  phone: string | null
  name: string
  is_admin: boolean
}

async function http<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  })
  if (r.status === 401) {
    throw new Error('nao_autenticado')
  }
  if (!r.ok) {
    const txt = await r.text().catch(() => '')
    throw new Error(`HTTP ${r.status}: ${txt}`)
  }
  return r.json() as Promise<T>
}

export async function getMe(): Promise<Me> {
  const { user } = await http<{ user: Me }>('/api/me')
  return user
}

export type ProdutoResumo = {
  codigo: string
  nome: string
  preco: string
  estoque: number
  grupo: string | null
  categoria_nome: string | null
  grupo_nome: string | null
  marca: string | null
  tem_meta: boolean
  qtd_imagens: number
  qtd_atributos: number
  atualizado_em: string
}

export type ListarProdutosParams = {
  q?: string
  incompletos?: boolean
  limit?: number
  offset?: number
  grupo?: string
  enriquecimento?: string
  estoque_min?: number
  estoque_max?: number
  ordenar?: string
  direcao?: 'asc' | 'desc'
}

export async function listarProdutos(params: ListarProdutosParams = {}): Promise<{ data: ProdutoResumo[]; total: number; limit: number; offset: number }> {
  const qs = new URLSearchParams()
  if (params.q) qs.set('q', params.q)
  if (params.incompletos) qs.set('incompletos', 'true')
  if (params.limit) qs.set('limit', String(params.limit))
  if (params.offset) qs.set('offset', String(params.offset))
  if (params.grupo) qs.set('grupo', params.grupo)
  if (params.enriquecimento) qs.set('enriquecimento', params.enriquecimento)
  if (params.estoque_min != null) qs.set('estoque_min', String(params.estoque_min))
  if (params.estoque_max != null) qs.set('estoque_max', String(params.estoque_max))
  if (params.ordenar) qs.set('ordenar', params.ordenar)
  if (params.direcao) qs.set('direcao', params.direcao)
  return http(`/api/produtos${qs.toString() ? `?${qs}` : ''}`)
}

export type GrupoErp = {
  codigo: string
  nome: string
  pai: string | null
  nivel: number
  total: number
  total_arvore: number
}

export async function listarGrupos(): Promise<GrupoErp[]> {
  return http('/api/produtos/grupos')
}

export type ProdutoDetalhe = {
  produto: {
    codigo: string
    nome: string
    descricao: string | null
    descricao_curta: string | null
    preco_venda: string
    estoque_atual: number
    grupo: string | null
    categoria_nome: string | null
    departamento: string | null
    marca: string | null
    marca_override: string | null
    peso: string | null
    peso_override: string | null
    altura: number | null
    largura: number | null
    comprimento: number | null
    descricao_longa: string | null
    bullets: string[] | null
    atualizado_em: string
  }
  imagens: Array<{
    id: number
    ordem: number
    url: string
    alt: string | null
    tipo: string | null
    canais: string[]
    largura: number | null
    altura: number | null
  }>
  atributos: Array<{ id: number; chave: string; valor: unknown; ordem: number }>
  enrich: EnrichData | null
  equivalencia: {
    ancora_id: string | null
    codigo_compra: string | null
    nome: string | null
    sem_correlacao: boolean
  } | null
}

export type EnrichData = {
  categoria: string | null
  subcategoria: string | null
  especie: string | null
  especie_alvo: string | null
  fase_vida: string | null
  porte: string | null
  faixa_qualidade: string | null
  marca: string | null
  fabricante: string | null
  principio_ativo: string | null
  concentracao: string | null
  forma_farmaceutica: string | null
  via_administracao: string | null
  apresentacao: string | null
  peso_min: number | null
  peso_max: number | null
  volume_valor: string | null
  volume_unidade: string | null
  ficha_tecnica: Record<string, unknown> | null
  // Proveniência por-campo (ADR-0024): chave = campo, valor = quem gravou aquele fato.
  fontes: Record<string, FieldFonte> | null
  fonte_classificacao: 'deterministico' | 'ia' | 'manual' | null
  revisado_por: string | null
  revisado_em: string | null
  classificado_em: string | null
}

export type FieldFonte = {
  fonte: 'erp' | 'deterministico' | 'ia' | 'ml' | 'fabricante' | 'manual'
  humano?: boolean
  em?: string
  deletado?: boolean
}

export type EnrichInput = Partial<Omit<EnrichData, 'fontes' | 'fonte_classificacao' | 'revisado_por' | 'revisado_em' | 'classificado_em'>>

// Diff por-campo devolvido pelo gate (ADR-0024): só os campos que realmente mudaram,
// já com a normalização do gate aplicada ao 'para'.
export type EnrichDiff = Record<string, { de: unknown; para: unknown }>

// Entrada do dicionário canônico de atributos (ADR-0024 Onda 3) — dirige o template
// da página: tipo do campo, enum do vocabulário do bot, destino (coluna|ficha) e ordem.
export type DicionarioEntry = {
  chave: string
  rotulo: string
  tipo: 'texto' | 'numero' | 'booleano' | 'lista'
  destino: 'coluna' | 'ficha'
  coluna_fisica: string | null
  enum_valores: string[] | null
  sinonimos: string[] | null
  ordem: number
}

export async function buscarDicionarioTemplate(): Promise<DicionarioEntry[]> {
  const { template } = await http<{ template: DicionarioEntry[] }>('/api/produtos/dicionario/template')
  return template ?? []
}

export async function salvarEnrich(codigo: string, payload: EnrichInput): Promise<EnrichDiff> {
  const { diff } = await http<{ ok: boolean; diff: EnrichDiff }>(
    `/api/produtos/${encodeURIComponent(codigo)}/enrich`,
    { method: 'PUT', body: JSON.stringify(payload) },
  )
  return diff ?? {}
}

export type FontesPesquisaIa = {
  fabricante: { consultado: boolean; encontrado: boolean; url: string | null; total_pares: number }
  ml_catalogo: {
    consultado: boolean
    encontrado: boolean
    catalog_product_id: string | null
    titulo_oficial: string | null
    total_atributos: number
  }
  web_search: { usado: boolean }
}

export type MedicamentoIa = {
  principio_ativo: string | null
  concentracao: string | null
  forma_farmaceutica: string | null
  via_administracao: string | null
  ficha: Record<string, string>
  // Extensões da pesquisa externa (backend novo; opcionais p/ compat)
  apresentacao?: string | null
  fabricante?: string | null
  especie?: string | null
  fontes?: FontesPesquisaIa
}

export async function sugerirMedicamentoIA(codigo: string): Promise<MedicamentoIa> {
  return http(`/api/produtos/${encodeURIComponent(codigo)}/ia/medicamento`, { method: 'POST' })
}

export async function buscarProduto(codigo: string): Promise<ProdutoDetalhe> {
  return http(`/api/produtos/${encodeURIComponent(codigo)}`)
}

export type MetaInput = {
  descricao_longa: string | null
  bullets: string[] | null
  marca_override: string | null
  peso: number | null
  altura: number | null
  largura: number | null
  comprimento: number | null
}

export async function salvarMeta(codigo: string, meta: MetaInput): Promise<void> {
  await http(`/api/produtos/${encodeURIComponent(codigo)}/meta`, {
    method: 'PUT',
    body: JSON.stringify(meta),
  })
}

export async function adicionarAtributo(
  codigo: string,
  chave: string,
  valor: unknown,
): Promise<{ id: number }> {
  return http(`/api/produtos/${encodeURIComponent(codigo)}/atributos`, {
    method: 'POST',
    body: JSON.stringify({ chave, valor }),
  })
}

export async function removerAtributo(id: number): Promise<void> {
  await http(`/api/produtos/atributos/${id}`, { method: 'DELETE' })
}

// ─── Imagens ─────────────────────────────────────────────────────────────────
export type ImagemUploadResultado = {
  id: number
  ordem: number
  url: string
  alt: string | null
  tipo: string | null
  canais: string[]
  largura: number | null
  altura: number | null
  bytes: number
}

export async function uploadImagem(
  codigo: string,
  arquivo: File,
  meta: { canais?: string[]; tipo?: string | null; alt?: string | null } = {},
): Promise<ImagemUploadResultado> {
  const fd = new FormData()
  fd.append('arquivo', arquivo)
  if (meta.canais) fd.append('canais', JSON.stringify(meta.canais))
  if (meta.tipo) fd.append('tipo', meta.tipo)
  if (meta.alt) fd.append('alt', meta.alt)

  const r = await fetch(`${API_BASE}/api/produtos/${encodeURIComponent(codigo)}/imagens`, {
    method: 'POST',
    body: fd,
    credentials: 'include',
  })
  if (!r.ok) {
    const txt = await r.text().catch(() => '')
    throw new Error(`HTTP ${r.status}: ${txt}`)
  }
  return r.json()
}

export async function atualizarImagem(
  id: number,
  patch: { canais?: string[]; tipo?: string | null; alt?: string | null; ordem?: number },
): Promise<void> {
  await http(`/api/produtos/imagens/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  })
}

export async function removerImagem(id: number): Promise<void> {
  await http(`/api/produtos/imagens/${id}`, { method: 'DELETE' })
}

export async function reordenarImagens(codigo: string, ids: number[]): Promise<void> {
  await http(`/api/produtos/${encodeURIComponent(codigo)}/imagens/reordenar`, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  })
}

// ─── IA ──────────────────────────────────────────────────────────────────────
export type IaDescricaoResultado = {
  descricao: string
  bullets: string[]
  alt_text: string
  usage: {
    input_tokens: number
    output_tokens: number
    cache_creation_input_tokens: number
    cache_read_input_tokens: number
  }
}

export async function gerarDescricaoIA(codigo: string): Promise<IaDescricaoResultado> {
  return http(`/api/produtos/${encodeURIComponent(codigo)}/ia/descricao`, {
    method: 'POST',
  })
}

// ─── Equivalência (âncora genérica nativa + membros de marca) ─────────────────
export type AncoraResumo = {
  id: string
  codigo_compra: string
  nome: string
  unidade: string | null
  origem_erp: string | null
  ativo: boolean
  qtd_membros: number
  estoque_total: string
}

export type MembroEquiv = {
  codigo: string
  nome: string
  grupo: string | null
  estoque_atual: string
  preco: string
  fator: string
  atualizado_em: string
  atualizado_por: string | null
}

export type GrupoEquivalencia = {
  ancora: {
    id: string
    codigo_compra: string
    nome: string
    unidade: string | null
    origem_erp: string | null
    ativo: boolean
    principio_ativo: string | null
    concentracao: string | null
  }
  membros: MembroEquiv[]
  estoque_total: number
  qtd_membros: number
}

export type CandidatoEquiv = {
  codigo: string
  nome: string
  grupo: string | null
  estoque_atual: string
  categoria_nome: string | null
  departamento: string | null
  principio_ativo: string | null
  concentracao: string | null
  match_principio: boolean
}

export type ProdutoBuscaEquiv = {
  codigo: string
  nome: string
  grupo: string | null
  estoque_atual: string
  ja_vinculado: boolean
  ancora_id: string | null
}

export async function listarAncoras(
  q?: string,
  apenasComMembros?: boolean,
): Promise<AncoraResumo[]> {
  const qs = new URLSearchParams()
  if (q) qs.set('q', q)
  if (apenasComMembros) qs.set('apenas_com_membros', 'true')
  return http(`/api/equivalencia/ancoras${qs.toString() ? `?${qs}` : ''}`)
}

export async function criarAncora(
  nome: string,
  unidade?: string,
): Promise<{ id: string; codigo_compra: string }> {
  return http(`/api/equivalencia/ancoras`, {
    method: 'POST',
    body: JSON.stringify({ nome, unidade: unidade || null }),
  })
}

export async function atualizarAncora(
  id: string,
  patch: { nome?: string; unidade?: string; codigo_compra?: string; ativo?: boolean; principio_ativo?: string | null; concentracao?: string | null },
): Promise<void> {
  await http(`/api/equivalencia/ancoras/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  })
}

export async function sugerirPrincipioIA(id: string): Promise<{ principio_ativo: string | null; concentracao: string | null }> {
  return http(`/api/equivalencia/ancora/${encodeURIComponent(id)}/ia-principio`, { method: 'POST' })
}

export async function removerAncora(id: string): Promise<void> {
  await http(`/api/equivalencia/ancoras/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export async function buscarAncora(id: string): Promise<GrupoEquivalencia> {
  return http(`/api/equivalencia/ancora/${encodeURIComponent(id)}`)
}

export async function listarSugestoes(
  id: string,
): Promise<{ ancora: { id: string; nome: string }; tokens: string[]; principio: string | null; candidatos: CandidatoEquiv[] }> {
  return http(`/api/equivalencia/ancora/${encodeURIComponent(id)}/sugestoes`)
}

export async function buscarProdutosEquiv(q: string): Promise<ProdutoBuscaEquiv[]> {
  return http(`/api/equivalencia/produtos?q=${encodeURIComponent(q)}`)
}

export async function vincularMembro(codigo_membro: string, ancora_id: string): Promise<void> {
  await http(`/api/equivalencia/membros`, {
    method: 'POST',
    body: JSON.stringify({ codigo_membro, ancora_id: Number(ancora_id) }),
  })
}

export async function descartarMembro(codigo_membro: string): Promise<void> {
  await http(`/api/equivalencia/membros`, {
    method: 'POST',
    body: JSON.stringify({ codigo_membro, sem_correlacao: true }),
  })
}

export async function removerMembro(codigo_membro: string): Promise<void> {
  await http(`/api/equivalencia/membros/${encodeURIComponent(codigo_membro)}`, {
    method: 'DELETE',
  })
}
